import { useIsDcParameterRegisteredQuery, useRegisterDcParameterMutation } from '@/core/services/dmp-connect-js2'
import { useGetDmpConnectDcParameterQuery } from '@/core/services/dmp-connect'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectDMPConnectOk, setDcParameter } from '@/redux/reducers/dmp-connect.reducer'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'
import { useLingui } from '@lingui/react'
import { t } from '@lingui/macro'

export function useDmpConnectDcParameter() {
  const lingui = useLingui()
  const dispatch = useAppDispatch()
  const connectorOk = useAppSelector(selectDMPConnectOk)

  const { data: isConnectorOk, error: connectorOkError } = useIsDcParameterRegisteredQuery('', { skip: !connectorOk })
  const { data: dcParameter, error: dcParameterError } = useGetDmpConnectDcParameterQuery()
  const { data: dcParameterRegistered, error: dcParameterRegisteredError } = useIsDcParameterRegisteredQuery(dcParameter ?? skipToken, {
    skip: !isConnectorOk,
    selectFromResult: ({ data, ...rest }) => ({ ...rest, data: data?.i_registered }),
  })

  const [registerDcParameter, { error: registerDcParameterError }] = useRegisterDcParameterMutation()

  const [isLoadingDcParameter, setIsLoadingDcParameter] = useState(dcParameterRegistered !== 1)

  const error = useMemo(() => {
    if (connectorOkError) return lingui._(t`Failed to connect to DMP Connect`)
    if (dcParameterError) return lingui._(t`Failed to get DMP Connect parameter`)
    if (dcParameterRegisteredError) return lingui._(t`Failed to check DMP Connect DC parameter`)
    if (registerDcParameterError) return lingui._(t`Failed to register DMP Connect DC parameter`)

    return undefined
  }, [lingui, connectorOkError, dcParameterError, dcParameterRegisteredError, registerDcParameterError])

  useEffect(() => {
    if (!dcParameter) return

    if (dcParameterRegistered === 1) {
      dispatch(setDcParameter(dcParameter))
      setIsLoadingDcParameter(false)
      return
    }

    if (dcParameterRegistered === 0) {
      registerDcParameter(dcParameter)
        .unwrap()
        .then(() => setIsLoadingDcParameter(false))
        .catch(() => setIsLoadingDcParameter(false))
    }
  }, [dcParameter, dcParameterRegistered, dispatch, registerDcParameter])

  return { dcParameterRegistered, error, isLoadingDcParameter }
}
